import { Control, Controller } from 'react-hook-form'

import {
  MultiSelectInput,
  MultiSelectInputProps,
  MultiSelectOption,
} from '@hivewatch/dark'

type OwnedProps = 'selected' | 'onToggleOption' | 'onRemove' | 'onClear'

export interface ControlledMultiSelectInputIProps
  extends Omit<MultiSelectInputProps, OwnedProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  name: string
  isRequired?: boolean
  defaultValue?: MultiSelectOption[]
}

/**
 * Binds the dark `MultiSelectInput` to react-hook-form via `Controller`. The
 * field stores the full `MultiSelectOption[]` — the same shape react-select
 * stored on the legacy multi-selects — so existing submit handlers keep
 * reading `values.<name>.map(({ value }) => value)` unchanged.
 *
 * `MultiSelectInput` reports selection as a `string[]` of values, so the
 * options passed in are the authority for turning those back into full
 * options; a selected value missing from `options` is preserved as-is.
 */
export const ControlledMultiSelectInput = ({
  control,
  name,
  isRequired = false,
  defaultValue = [],
  options = [],
  ...rest
}: ControlledMultiSelectInputIProps) => (
  <Controller
    control={control}
    defaultValue={defaultValue}
    name={name}
    render={({ value, onChange }) => {
      const selectedOptions: MultiSelectOption[] = value ?? []

      const toOption = (optionValue: string): MultiSelectOption =>
        options.find((option) => option.value === optionValue) ??
        selectedOptions.find((option) => option.value === optionValue) ?? {
          label: optionValue,
          value: optionValue,
        }

      return (
        <MultiSelectInput
          {...rest}
          onClear={() => onChange([])}
          onRemove={(optionValue) =>
            onChange(
              selectedOptions.filter((option) => option.value !== optionValue)
            )
          }
          onToggleOption={(optionValue) =>
            onChange(
              selectedOptions.some((option) => option.value === optionValue)
                ? selectedOptions.filter(
                    (option) => option.value !== optionValue
                  )
                : [...selectedOptions, toOption(optionValue)]
            )
          }
          options={options}
          selected={selectedOptions.map(({ value: v }) => v)}
          selectedOptions={selectedOptions}
        />
      )
    }}
    rules={isRequired ? { validate: (v) => (v?.length ?? 0) > 0 } : undefined}
  />
)
